import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";
import "katex/dist/katex.min.css"; // For math rendering if used in questions
import renderMathInElement from "katex/contrib/auto-render";
import { renderToString } from "katex";
import { API_BASE } from "../../config";
import Footer from "../homepage/footer";
import RenderBlocks from "../../components/RenderBlocks";
import MathAnswerField from "../../components/MathAnswerField.jsx";

const QuizTopic = ({ userUID }) => {
  const { topicId } = useParams(); // Get topic ID from URL
  const navigate = useNavigate();

  // State declarations
  const [questions, setQuestions] = useState([]); // Holds the list of questions
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Tracks the index of the current question
  const [userAnswers, setUserAnswers] = useState({}); // Stores user's selected answers
  const [revealedAnswers, setRevealedAnswers] = useState({}); // Tracks whether the correct answer has been revealed
  const [attempts, setAttempts] = useState({}); // Tracks number of attempts per question
  const [feedback, setFeedback] = useState(null); // Stores feedback message after answer submission
  const [topicName, setTopicName] = useState(""); // Optionally display topic name
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  // MathQuill replaces prior custom input/keypad

  // Constants
  const maxScore = 100;
  const maxAttemptsPerQuestion = 1;

  // Best-effort LaTeX normalization for cases where backslashes are missing (e.g., tfrac -> \tfrac)
  const normalizeLatexText = (text) => {
    if (!text || typeof text !== "string") return text || "";
    let s = text;
    // Recover common commands missing leading backslash
    s = s.replace(/(^|[^\\])(tfrac)(\s*\{)/g, "$1\\$2$3");
    s = s.replace(/(^|[^\\])(frac)(\s*\{)/g, "$1\\$2$3");
    s = s.replace(/(^|[^\\])(sqrt)(\s*\{)/g, "$1\\$2$3");
    s = s.replace(/\\\\/g, "\\"); // collapse double slashes like \\\circ -> \circ
    // Convert \cdot to \times for consistent multiplication symbol display
    s = s.replace(/\\cdot/g, "\\times");
    return s;
  };

  const renderOptionHtml = (option) => {
    const s = typeof option === "string" ? option : String(option ?? "");
    const match = s.match(/^([a-d]\)\s*)([\s\S]*)$/i);
    const prefix = match ? match[1] : "";
    const content = normalizeLatexText(match ? match[2] : s);
    try {
      const html = renderToString(content, { throwOnError: false });
      return { prefix, html };
    } catch {
      return { prefix, html: content };
    }
  };

  // Fetch questions when component loads
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/questions/random/${topicId}`);
        if (!response.ok) throw new Error("Failed to fetch questions");
        const questionData = await response.json();
        console.log("Fetched Questions:", questionData);
        setQuestions(questionData);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [topicId]);

  // Removed old outside-click handlers for dropdown menus

  // Render inline LaTeX contained in question_text using KaTeX auto-render
  const questionTextRef = useRef(null);
  const feedbackRef = useRef(null);
  useEffect(() => {
    try {
      if (questionTextRef.current) {
        renderMathInElement(questionTextRef.current, {
          delimiters: [
            { left: "$", right: "$", display: false },
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true }
          ],
          throwOnError: false
        });
      }
    } catch {}
  }, [currentQuestionIndex, questions]);

  // Render math inside feedback/explanation
  useEffect(() => {
    try {
      if (feedbackRef.current) {
        renderMathInElement(feedbackRef.current, {
          delimiters: [
            { left: "$", right: "$", display: false },
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true }
          ],
          throwOnError: false
        });
      }
    } catch {}
  }, [feedback]);

  // Handle user selecting an answer
  const handleAnswerChange = (answer) => {
    const questionId = questions[currentQuestionIndex]?.id;
    setUserAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  // MathQuill answer normalization for comparison
  const normalizeLatexAnswer = (latex) => {
    if (!latex) return "";
    let s = String(latex).trim();
    // Visual symbol normalization
    s = s
      .replace(/\\times|\\cdot|×/g, '*') // Handle both \times and \cdot as multiplication
      .replace(/\\div|÷/g, '/')
      .replace(/\\pi/g, 'pi')
      .replace(/\\pm/g, '+/-');
    // Fractions and powers -> plain
    s = s
      .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2')
      .replace(/\^\{([^}]+)\}/g, '^$1')
      .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt($1)');
    // Remove braces and collapse spaces
    s = s.replace(/[{}]/g, '');
    s = s.replace(/\s+/g, ' ').trim().toLowerCase();
    return s;
  };

  // Format answer for display with proper LaTeX rendering
  const formatAnswerForDisplay = (answer) => {
    if (!answer) return 'Not Available';
    // Normalize the answer first to fix backslash issues
    const normalized = normalizeLatexText(answer);
    // Check if answer contains LaTeX commands (like ^\circ, \frac, etc.)
    if (/\\[a-zA-Z]|\^|_/g.test(normalized)) {
      // Wrap LaTeX expressions in delimiters for renderMathInElement if not already wrapped
      if (normalized.includes('\\(') && normalized.includes('\\)')) {
        return normalized; // Already wrapped
      }
      return `\\(${normalized}\\)`;
    }
    return normalized;
  };

  // Render feedback message with LaTeX - fixes spacing and preserves LaTeX for auto-render
  const renderFeedbackMessage = (message) => {
    if (!message) return '';
    
    try {
      // Fix spacing issues while preserving LaTeX expressions
      let fixedText = message
        // Fix specific concatenated words
        .replace(/Thecorrectansweris/g, 'The correct answer is')
        .replace(/Explanation:/g, 'Explanation: ')
        // Fix broken words from LaTeX processing (common patterns)
        .replace(/\bA\s+ngles\b/g, 'Angles')
        .replace(/\ba\s+ngles\b/g, 'angles')
        .replace(/\bT\s+he\b/g, 'The')
        .replace(/\bt\s+he\b/g, 'the')
        .replace(/\bS\s+ame\b/g, 'Same')
        .replace(/\bs\s+ame\b/g, 'same')
        .replace(/\bS\s+egment\b/g, 'Segment')
        .replace(/\bs\s+egment\b/g, 'segment')
        .replace(/\bA\s+re\b/g, 'Are')
        .replace(/\ba\s+re\b/g, 'are')
        .replace(/\bE\s+qual\b/g, 'Equal')
        .replace(/\be\s+qual\b/g, 'equal')
        .replace(/\bB\s+oth\b/g, 'Both')
        .replace(/\bb\s+oth\b/g, 'both')
        .replace(/\bS\s+ubtend\b/g, 'Subtend')
        .replace(/\bs\s+ubtend\b/g, 'subtend')
        .replace(/\bC\s+hord\b/g, 'Chord')
        .replace(/\bc\s+hord\b/g, 'chord')
        .replace(/\ba\s+ngl\s+e\b/g, 'angle')
        .replace(/\bA\s+ngl\s+e\b/g, 'Angle')
        // Fix broken LaTeX commands
        .replace(/\\a\s+ngl\s+e\b/g, '\\angle')
        .replace(/\\cir\s+c\b/g, '\\circ')
        // Fix spacing around capital letters (like "A DB" -> "ADB")
        .replace(/\b([A-Z])\s+([A-Z])\s+([A-Z])\b/g, '$1$2$3')
        .replace(/\b([A-Z])\s+([A-Z])\b/g, '$1$2')
        // Fix spacing around LaTeX commands (but preserve the commands)
        .replace(/([a-zA-Z])\s+(\\[a-zA-Z]+)/g, '$1 $2')
        .replace(/(\\[a-zA-Z]+)\s+([a-zA-Z])/g, '$1 $2')
        // Fix spacing around numbers and LaTeX
        .replace(/(\d+)\s+(\\[a-zA-Z]+)/g, '$1 $2')
        .replace(/(\\[a-zA-Z]+)\s+(\d+)/g, '$1 $2')
        // Fix spacing around numbers
        .replace(/(\d+)cm/g, '$1 cm')
        .replace(/(\d+)([a-zA-Z]{2,})/g, '$1 $2')
        .replace(/([a-zA-Z]{2,})(\d+)/g, '$1 $2')
        // Fix LaTeX backslash issues (fix double backslashes)
        .replace(/\\\\/g, '\\');
      
      // Normalize LaTeX text
      fixedText = normalizeLatexText(fixedText);
      
      // Return plain text - renderMathInElement will handle LaTeX rendering
      return fixedText;
    } catch {
      return message; // Fallback to plain text
    }
  };

  // removed old placeholder-based system

  // Move to the next question or finish the quiz (requires feedback to proceed)
  const moveToNextQuestion = () => {
    if (!feedback) return; // must submit first
    if (currentQuestionIndex + 1 < questions.length) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setFeedback(null); // Clear feedback when moving to next question
    } else {
      calculateFinalScore(); // If last question, calculate score
    }
  };

  // Calculate the user's total quiz score
  const calculateFinalScore = async () => {
    let correctAnswers = 0;

    questions.forEach((q) => {
      const questionId = q.id;
      // Count only if answer is correct and not revealed
      if (!revealedAnswers[questionId] && userAnswers[questionId]?.trim() === q.correct_answer?.trim()) {
        correctAnswers++;
      }
    });

    const finalScore = Math.round((correctAnswers / questions.length) * 100);

    // Save quiz attempt before showing final score
    await logQuizAttempt(finalScore, 120);

    setFeedback({ message: `🎉 Quiz Completed! Your score: ${finalScore}/${maxScore}.`, isCorrect: true });

    // Redirect back to topics page after 2s
    setTimeout(() => navigate("/topicspage"), 2000);
  };

  // Save quiz attempt to the database
    const logQuizAttempt = async (finalScore, completionTime) => {
    try {
      // Get the most recent attempt number
      const response = await fetch(`${API_BASE}/api/quiz-attempts/${userUID}/${topicId}/latest-attempt`);
      let attemptNumber = 1;

      if (response.ok) {
        const data = await response.json();
        attemptNumber = (data.latest_attempt || 0) + 1;
      }

      // Log new attempt
      const logResponse = await fetch(`${API_BASE}/api/quiz-attempts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_uid: userUID,
          topic_id: topicId,
          score: finalScore,
          max_score: maxScore,
          attempt_number: attemptNumber,
          completion_time: completionTime,
          is_passed: finalScore >= 50,
          feedback: `You scored ${finalScore}/${maxScore}. Keep practicing!`,
        }),
      });

      if (!logResponse.ok) throw new Error("Failed to log quiz attempt");

      console.log("Quiz attempt logged successfully.");
    } catch (err) {
      console.error("Error logging quiz attempt:", err);
    }
  };

  // Handles when user submits an answer
  const handleSubmit = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    const questionId = currentQuestion.id;
    const userAnswer = (userAnswers[questionId] || "").trim();

    if (!userAnswer) {
      setFeedback({ message: "Please enter an answer!", isCorrect: false });
      return;
    }

    const normalizeChoice = (answer) => answer.replace(/^[a-d]\)\s*/, "").trim().toLowerCase();
    let isCorrect = false;
    if (Array.isArray(currentQuestion.options)) {
      isCorrect = normalizeChoice(userAnswer) === normalizeChoice(currentQuestion.correct_answer || "");
    } else {
      const lhs = normalizeLatexAnswer(userAnswer);
      const rhs = normalizeLatexAnswer(currentQuestion.correct_answer || "");
      isCorrect = lhs === rhs;
    }

    const currentAttempts = attempts[questionId] || 0;

    if (isCorrect) {
      setFeedback({
        message: `✅ Correct!\n\n📝 Explanation: ${normalizeLatexText(currentQuestion.explanation)}`,
        isCorrect: true,
      });
      setRevealedAnswers((prev) => ({ ...prev, [questionId]: false }));
    } else if (currentAttempts + 1 >= maxAttemptsPerQuestion) {
      setRevealedAnswers((prev) => ({ ...prev, [questionId]: true }));
      setFeedback({
        message: `📢 The correct answer is: ${formatAnswerForDisplay(currentQuestion.correct_answer)}\n\n📝 Explanation: ${normalizeLatexText(currentQuestion.explanation)}`,
        isCorrect: false,
      });
    } else {
      setFeedback({
        message: `❌ Incorrect! You have ${maxAttemptsPerQuestion - (currentAttempts + 1)} attempt(s) left.`,
        isCorrect: false,
      });
      setAttempts((prev) => ({ ...prev, [questionId]: currentAttempts + 1 }));
    }
  };

  // Render loading or error state
  if (loading) return <div className="text-center py-10 sm:py-20 text-teal-600 text-sm sm:text-base">Loading questions...</div>;
  if (error) return <div className="text-center py-10 sm:py-20 text-red-500 text-sm sm:text-base px-4">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center">
      <Helmet>
        <title>{topicName ? `${topicName} Quiz | MLMaths` : "Quiz | MLMaths"}</title>
        <link rel="canonical" href={`https://mlmaths.co.uk/quizzes/${topicId || ''}`} />
        <meta name="description" content={`Take a quiz on ${topicName || 'this topic'} to test your GCSE Maths knowledge and track your progress.`} />
      </Helmet>
      {/* Topic Title (optional) */}
      <h1 className="text-2xl sm:text-4xl font-bold text-teal-600 mt-6 sm:mt-10 px-4 text-center">{topicName}</h1>

      {/* Quiz UI */}
      <main className="flex flex-col items-center justify-center flex-grow w-full px-1 sm:px-2 md:px-4">
        {questions.length > 0 && (
          <div className="bg-white p-3 sm:p-6 md:p-8 lg:p-10 shadow-lg rounded-lg w-full max-w-6xl mt-2 sm:mt-4 md:mt-6 mx-auto min-h-[350px] sm:min-h-[500px] md:min-h-[500px] lg:min-h-[450px] xl:min-h-[400px] flex flex-col justify-start space-y-3 sm:space-y-4 md:space-y-5 lg:space-y-4">
            {/* Question Header */}
            <h2 className="text-lg sm:text-2xl font-semibold text-teal-600 mb-3 sm:mb-4">
              Question {currentQuestionIndex + 1}/{questions.length}
            </h2>

            {/* Question Text */}
            <div className="bg-gray-100 border-l-4 border-teal-500 p-2 sm:p-4 md:p-6 rounded-lg shadow-md w-full text-sm sm:text-base md:text-lg">
              <p ref={questionTextRef} className="font-semibold text-gray-800 leading-relaxed">
                {normalizeLatexText(questions[currentQuestionIndex]?.question_text || "")}
              </p>
            </div>

            {/* Visuals / Render Blocks */}
            <RenderBlocks blocks={questions[currentQuestionIndex]?.render_blocks} />

            {/* Options or Answer input depending on modality */}
            {Array.isArray(questions[currentQuestionIndex]?.options) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4 w-full">
                {questions[currentQuestionIndex].options.map((option, index) => {
                  const { prefix, html } = renderOptionHtml(option);
                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerChange(option)}
                      className={`bg-gray-200 text-sm sm:text-base md:text-lg py-2 sm:py-3 md:py-4 px-2 sm:px-4 md:px-6 rounded-lg shadow-md w-full transition-colors ${userAnswers[questions[currentQuestionIndex]?.id] === option ? "bg-teal-500 text-white" : "hover:bg-gray-300"}`}
                    >
                      <span>{prefix}</span>
                      <span dangerouslySetInnerHTML={{ __html: html }} />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="w-full">
                <MathAnswerField
                  value={userAnswers[questions[currentQuestionIndex]?.id] || ""}
                  onChange={(latex) => handleAnswerChange(latex)}
                  onEnter={handleSubmit}
                  disabled={!!feedback} // Disable input once feedback is shown
                />
              </div>
            )}

            {/* Feedback Box */}
            {feedback && (
              <div ref={feedbackRef} className={`text-xs sm:text-sm md:text-base p-2 sm:p-3 md:p-4 rounded-lg ${feedback.isCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                <div 
                  className="font-semibold leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderFeedbackMessage(feedback.message) }}
                />
              </div>
            )}

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
              <button 
                onClick={handleSubmit} 
                disabled={!!feedback}
                className={`w-full sm:w-1/2 py-2 sm:py-3 rounded-lg text-sm sm:text-base transition-colors ${feedback ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-teal-500 hover:bg-teal-600 text-white"}`}
              >
                Submit Answer
              </button>
              <button onClick={moveToNextQuestion} disabled={!feedback} className={`w-full sm:w-1/2 py-2 sm:py-3 rounded-lg text-sm sm:text-base transition-colors ${feedback ? "bg-gray-700 hover:bg-gray-800 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>
                Next Question
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default QuizTopic;